'use client';

import React, { PropsWithChildren, ReactNode } from 'react';
import { cn } from './cn';
import { CarouselContext, useCarouselContext } from './carousel-context';
import { useHeroCarousel } from './use-hero-carousel';
import { CarouselLabels } from './types';

interface CarouselProps extends PropsWithChildren {
  /** Number of slides — required so dots render correctly during SSR. */
  slideCount: number;
  loop?: boolean;
  autoplayDelay?: number;
  labels?: CarouselLabels;
  /** Layout classes for the carousel region. `relative` is always applied. */
  className?: string;
}

/**
 * Provider + accessible region wrapper. Holds the carousel behaviour in context
 * so the sub-components (`CarouselViewport`, `CarouselPrevious`, `CarouselNext`,
 * `CarouselDots`) can be composed in any layout a portal wants. Renders a
 * positioned region so absolutely-placed arrows have an anchor without
 * depending on the consumer's wrapper.
 *
 * The sub-components are separate named exports (not static properties on
 * `Carousel`) because static properties are stripped when a 'use client'
 * component crosses the Next.js server/client boundary.
 */
export function Carousel({
  slideCount,
  loop,
  autoplayDelay,
  labels,
  className,
  children,
}: CarouselProps): React.ReactElement {
  const api = useHeroCarousel({ slideCount, loop, autoplayDelay, labels });

  return (
    <CarouselContext.Provider value={api}>
      <div
        className={cn('relative', className)}
        role='region'
        aria-roledescription='carousel'
        aria-label={api.labels.regionLabel}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

interface ViewportProps extends PropsWithChildren {
  className?: string;
  trackClassName?: string;
}

/** Scroll viewport. Slides are passed as children (kept as server components). */
export function CarouselViewport({
  className,
  trackClassName,
  children,
}: ViewportProps): React.ReactElement {
  const { viewportRef } = useCarouselContext();
  return (
    <div className={cn('overflow-hidden', className)} ref={viewportRef}>
      <div className={cn('flex', trackClassName)}>{children}</div>
    </div>
  );
}

interface ArrowProps {
  className?: string;
  /** Custom icon. Falls back to a chevron when omitted. */
  children?: ReactNode;
}

const PrevChevron = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={32}
    height={32}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M15 18l-6-6 6-6' />
  </svg>
);

const NextChevron = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width={32}
    height={32}
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M9 18l6-6-6-6' />
  </svg>
);

export function CarouselPrevious({ className, children }: ArrowProps): React.ReactElement | null {
  const { canPrev, scrollPrev, slideCount, labels } = useCarouselContext();
  if (slideCount <= 1) return null;
  return (
    <button
      type='button'
      aria-label={labels.prevSlide}
      onClick={scrollPrev}
      disabled={!canPrev}
      className={cn('disabled:pointer-events-none disabled:opacity-40', className)}
    >
      {children ?? PrevChevron}
    </button>
  );
}

export function CarouselNext({ className, children }: ArrowProps): React.ReactElement | null {
  const { canNext, scrollNext, slideCount, labels } = useCarouselContext();
  if (slideCount <= 1) return null;
  return (
    <button
      type='button'
      aria-label={labels.nextSlide}
      onClick={scrollNext}
      disabled={!canNext}
      className={cn('disabled:pointer-events-none disabled:opacity-40', className)}
    >
      {children ?? NextChevron}
    </button>
  );
}

interface DotRenderProps {
  index: number;
  isActive: boolean;
  id: string;
  controls: string;
  label: string;
  onClick: () => void;
}

interface DotsProps {
  className?: string;
  dotClassName?: string;
  activeDotClassName?: string;
  /** Full control over a single dot's markup, for portals that need it. */
  renderDot?: (props: DotRenderProps) => ReactNode;
}

export function CarouselDots({
  className,
  dotClassName,
  activeDotClassName,
  renderDot,
}: DotsProps): React.ReactElement | null {
  const { slideCount, selectedIndex, scrollTo, labels, getSlideId, getTabId } =
    useCarouselContext();
  if (slideCount <= 1) return null;

  return (
    <div className={cn('flex w-full items-center justify-center gap-4', className)} role='tablist'>
      {Array.from({ length: slideCount }, (_, index) => {
        const isActive = selectedIndex === index;
        const dotProps: DotRenderProps = {
          index,
          isActive,
          id: getTabId(index),
          controls: getSlideId(index),
          label: `${labels.goToSlide} ${index + 1}`,
          onClick: () => scrollTo(index),
        };

        if (renderDot) {
          return <React.Fragment key={index}>{renderDot(dotProps)}</React.Fragment>;
        }

        return (
          <button
            key={index}
            id={dotProps.id}
            type='button'
            role='tab'
            aria-controls={dotProps.controls}
            aria-label={dotProps.label}
            aria-selected={isActive}
            onClick={dotProps.onClick}
            className={cn(
              'h-5 w-5 rounded-full border border-solid border-[#b3b3b3] hover:bg-[#b3b3b3]',
              dotClassName,
              isActive && cn('border-primary bg-primary hover:bg-primary', activeDotClassName),
            )}
          />
        );
      })}
    </div>
  );
}
